import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CloseGlyph } from './parts';
import { NookGlyph } from './NookGlyph';
import { useHangMove } from '../plane/Hang';
import '../plane/hang.css';

/** What a panel's head offers for its subject's own page: a link, or a press. */
export type FullScreen = { to: string } | { onPress: () => void };

function FullScreenLink({ to }: { to: FullScreen }) {
  const label = (
    <>
      <span aria-hidden="true">↗</span> Full screen
    </>
  );
  return 'to' in to ? (
    <Link className="hang-full" to={to.to}>
      {label}
    </Link>
  ) : (
    <button type="button" className="hang-full" onClick={to.onPress}>
      {label}
    </button>
  );
}

function Close({ onClose }: { onClose: () => void }) {
  return (
    <button type="button" className="hang-x" aria-label="Close" onClick={onClose}>
      <CloseGlyph />
    </button>
  );
}

/** The head of a card's details: what it is on the left, Full screen and × at the right. Full
 *  screen is a press inside the panel; the panel itself opens no page. */
export function DetailsHead({ lead, fullScreen, onClose }: { lead?: ReactNode; fullScreen?: FullScreen; onClose: () => void }) {
  const move = useHangMove();
  return (
    <div className={`hang-head${move ? ' movable' : ''}`} {...move?.handle}>
      <span>{lead}</span>
      <span className="hang-head-actions">
        {fullScreen && <FullScreenLink to={fullScreen} />}
        <Close onClose={onClose} />
      </span>
    </div>
  );
}

/** A wide panel's body: what it is on the left, what it holds on the right. With nothing held it is
 *  the narrow panel it always was. */
export function WideColumns({ about, holds }: { about: ReactNode; holds?: ReactNode }) {
  if (!holds) return <>{about}</>;
  return (
    <div className="hang-cols">
      <div className="hang-col-about">{about}</div>
      <div className="hang-col-holds">{holds}</div>
    </div>
  );
}

/**
 * A nook's details, hung off its card: the glyph, name, what waits on you, where it stands, its
 * Next and the card's secondary lines.
 */
export function NookDetails({
  holds,
  name,
  ink,
  waiting,
  glance,
  next,
  lines,
  fullScreen,
  onClose,
}: {
  /** What it holds as real cards: in the right column, beside everything about it on the left. */
  holds?: ReactNode;
  /** Empty for a nook nobody named. */
  name: string;
  ink?: string;
  waiting: number;
  glance?: string | null;
  next?: string | null;
  lines?: ReactNode;
  fullScreen?: FullScreen;
  onClose: () => void;
}) {
  return (
    <>
      <DetailsHead lead={<NookGlyph size={18} ink={ink} />} fullScreen={fullScreen} onClose={onClose} />
      <WideColumns
        about={
          <>
            <h2 className="hang-name">
              <span className={name ? undefined : 'is-unnamed'}>{name || 'Unnamed'}</span>
            </h2>
            {waiting > 0 && <p className="hang-waiting">{waiting} waiting on you</p>}
            {glance && <p className="hang-prose">{glance}</p>}
            {next && (
              <p className="hang-next">
                <span className="hang-label">Next</span>
                {next}
              </p>
            )}
            {lines && <div className="hang-lines">{lines}</div>}
          </>
        }
        holds={holds}
      />
    </>
  );
}

/**
 * The shell of a note hung off its sticky: a head with the tint swatches and the ways out, and the
 * writing under it. The tint's chip and edge are the host's, as [Hang]'s `tint`. [onGripDown] draws the size grip where the note can
 * be resized.
 */
export function NoteShell({
  lead,
  actions,
  fullScreen,
  onClose,
  onGripDown,
  children,
}: {
  /** Left of the head: the tint swatches, or whatever says whose note this is. */
  lead?: ReactNode;
  actions?: ReactNode;
  fullScreen?: FullScreen;
  onClose: () => void;
  onGripDown?: (event: ReactPointerEvent<HTMLElement>) => void;
  children: ReactNode;
}) {
  const move = useHangMove();
  return (
    <>
      <div className={`hang-note-head${move ? ' movable' : ''}`} {...move?.handle}>
        <span className="hang-note-lead">{lead}</span>
        {actions}
        {fullScreen && <FullScreenLink to={fullScreen} />}
        <Close onClose={onClose} />
      </div>
      <div className="hang-note-body">{children}</div>
      {onGripDown && <span className="hang-grip" aria-label="Resize" role="separator" onPointerDown={onGripDown} />}
    </>
  );
}
